﻿import { Language, TimeZone } from '../ProfileApi';
import { UserProfile } from '../models/profile';
import { ProfileContextLabels } from '../ProfileContext';
import React, { MouseEvent, useState } from 'react';
import { useImpersonatedCountryContext } from '../../HeadBar';
import { useEllipsesWorkaround } from '../hooks/useEllipsesWorkaround';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { Spacer } from '../../../Spacer';
import Typography from '@mui/material/Typography';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TranslatedProfileContextMenu from './ProfileContextMenu';
import ImpersonationLabel from './ImpersonationLabel';
import ImpersonatedCountryFlag from './ImpersonatedCountryFlag';
import { useUserContext } from '../../../../../../../shared/context/user-context';

type ProfileContextSmallerScreenProps = {
    availableLanguages: Language[];
    availableTimeZones: TimeZone[];
    userProfile: UserProfile;
    labels: ProfileContextLabels;
};

function ProfileContextSmallerScreen({
    labels,
    userProfile,
    availableTimeZones,
    availableLanguages
}: ProfileContextSmallerScreenProps) {
    let { user } = useUserContext();
    let [showMenu, setShowMenu] = useState<boolean>(false);
    let [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(null);

    let { impersonatedCountry } = useImpersonatedCountryContext();

    function handleClick(event: MouseEvent) {
        setAnchorElement(event.currentTarget);
        setShowMenu((state) => !state);
    }

    function handleMenuClose() {
        setShowMenu(false);
        setAnchorElement(null);
    }

    let { outerContainerRef, innerContainerRef, nameMaxWidth } = useEllipsesWorkaround();

    return (
        <>
            <Box
                ref={outerContainerRef}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    flex: '1 0 auto'
                }}
            >
                <Box ref={innerContainerRef}>
                    {user.details.isImpersonated ? (
                        <>
                            <Box sx={{ display: 'flex', flex: '1 0 auto' }} />
                            <ImpersonationLabel label={labels.loggedInAs} />
                        </>
                    ) : null}
                    <IconButton
                        sx={{
                            color: '#fff',
                            flex: '1 0 auto',
                            overflow: 'auto',
                            whiteSpace: 'nowrap',
                            maxWidth: `${nameMaxWidth}px`
                        }}
                        onClick={handleClick}
                    >
                        {impersonatedCountry ? (
                            <ImpersonatedCountryFlag countryId={impersonatedCountry.id} />
                        ) : null}
                        <Typography
                            variant="body1"
                            sx={{
                                fontFamily: 'Gilroy-Regular',
                                fontSize: '12px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}
                        >
                            {user.details.isImpersonated ? user.details.name : userProfile.name}
                        </Typography>
                        {showMenu ? (
                            <>
                                <Spacer />
                                <ExpandLessIcon />
                            </>
                        ) : (
                            <>
                                <Spacer />
                                <ExpandMoreIcon />
                            </>
                        )}
                    </IconButton>
                </Box>
            </Box>
            <TranslatedProfileContextMenu
                open={showMenu}
                onClose={handleMenuClose}
                anchorElement={anchorElement}
                availableLanguages={availableLanguages}
                availableTimeZones={availableTimeZones}
                userProfile={userProfile}
            />
        </>
    );
}

export default ProfileContextSmallerScreen;
